import { useAuthenticationContext } from "@climatepartner/app-shell/authentication-context";
import { Spinner } from "@climatepartner/components/Spinner";
import { CpServices, getServiceUrls } from "@climatepartner/utils";
import { CSRD_SERVICE_NAME } from "@service/string-constants";
import { QueryClient, useQueryClient } from "@tanstack/react-query";
import { ReactElement, useEffect, useMemo } from "react";

import { setClientsApiToken } from "../api/api-clients";
import { ENVIRONMENT } from "../config/environment";
import { CFC_ROLE } from "./guards/admin-roles-authorisation.guard.helpers";
import { UserContext } from "./user-context";
import { getAllowedSolutions, isEmployeeInternal, isTrialUser } from "./user-utils";

const activeAccountIdByQueryClient = new WeakMap<QueryClient, string>();

type Props = {
  children: ReactElement | ReactElement[];
};

export const UserProvider = ({ children }: Props): ReactElement => {
  const { user, isAuthenticated, actions } = useAuthenticationContext();
  const { hasPermission, logout } = actions;

  const queryClient = useQueryClient();

  useEffect(() => {
    if (user !== null && isAuthenticated) {
      setClientsApiToken(user.accessToken);

      const accountId = user.activeAccount.accountId;
      const previousAccountId = activeAccountIdByQueryClient.get(queryClient);
      if (previousAccountId !== undefined && previousAccountId !== accountId) {
        queryClient.clear();
      }
      activeAccountIdByQueryClient.set(queryClient, accountId);
    }
  }, [user, isAuthenticated, queryClient]);

  const isEmissionFactorAdmin = useMemo(() => hasPermission(CFC_ROLE.EMISSION_FACTOR_ADMIN), [hasPermission]);

  const value = useMemo(() => {
    const customerId = user?.activeAccount.accountId ?? null;

    const hasAccessToOrganization = (organizationId: string): boolean => {
      return user.activeAccount.accountId === organizationId;
    };

    const services: CpServices = getServiceUrls({
      env: ENVIRONMENT,
      userHasPermission: hasPermission,
    }).reduce((acc, service) => {
      if (service.userHasAccess) {
        acc[service.id] = service.url;
      }
      return acc;
    }, {} as CpServices);

    const isTrialCustomer = isTrialUser(hasPermission);
    let isCsrdEnabled = services[CSRD_SERVICE_NAME] !== undefined;
    if (isTrialCustomer) {
      isCsrdEnabled = false;
    }
    const allowedSolutions = getAllowedSolutions(isEmployeeInternal(hasPermission), isTrialCustomer, services);

    return {
      user: user
        ? {
            ...user,
            customerId,
            isAuthenticated,
            isEmissionFactorAdmin,
            services,
            allowedSolutions,
            isCsrdEnabled,
            isTrialUser: isTrialCustomer,
          }
        : null,
      hasAccessToOrganization,
      hasPermission,
      logout: logout,
    };
  }, [user, hasPermission, isAuthenticated, isEmissionFactorAdmin, logout]);

  if (!user || !isAuthenticated) {
    return <Spinner isFixed />;
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export type { CfcUser, UserContextType } from "./user-context";
